<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAppStore } from '@/stores/appStore';
import { useShellStore } from '@/stores/shellStore';
import logoSrc from '@/assets/logo.webp';

/**
 * Top bar: brand, breadcrumbs (with the current module's icon) and the slot a module docks its own controls
 * in. Nothing else.
 *
 * 2026-09-10 shell feedback: the environment badge is gone (`shell.env` stays in the store for other callers)
 * and the account menu moved to the foot of the sidebar.
 *
 * Round 6: the attention pill is gone too — "I still don't see the yellow overdue orders in the left side
 * menu; you can remove the tile from the top right". A module's attention registration
 * (`composables/useModuleAttention`) now shows as that module's yellow badge in the sidebar, beside the
 * module it belongs to, and it stays visible from anywhere in the app instead of only while that module is
 * open. `shellStore.moduleAttention` is unchanged; this bar simply no longer reads it, so the pill's
 * dropdown, its document-click handler and its Escape handler went with it.
 *
 * Round 3: the bar carries a slot (`#shell-topbar-slot`, see `topbarSlot.ts`) after the breadcrumb. A module
 * with a control row of its own teleports it in there — the production scheduler used to draw a second, grey
 * bar directly under this one, which is the "two header bars" the owner objected to.
 *
 * Round 4: the hamburger this bar carried between the brand block and the breadcrumb is gone — "the hamburger
 * to close it is not needed, we have the collapse button on the main menu anyway". `shell.collapsed` now has
 * exactly one control, the toggle at the foot of `AppSidebar`, and the breadcrumb starts straight after the
 * brand block. The sidebar keeps `id="app-sidebar"`; nothing points `aria-controls` at it from here any more.
 *
 * Round 2: the disabled search stub is gone — it duplicated the real, working search each module's own header
 * carries.
 */
const appStore = useAppStore();
const shell = useShellStore();
const route = useRoute();

const current = computed(() => appStore.currentApp);
const categoryLabel = computed(() => {
  const id = appStore.currentAppId;
  for (const item of appStore.resolvedSidebar) if (item.type === 'category' && item.children.some((c) => c.id === id)) return item.label;
  return null;
});
/** The ModuleConfig behind the current app (it carries `tabs`; AppDefinition does not). */
const currentModule = computed(() => {
  const id = appStore.currentAppId;
  for (const item of appStore.resolvedSidebar) {
    if (item.type === 'module' && item.module.id === id) return item.module;
    if (item.type === 'category') { const m = item.children.find((c) => c.id === id); if (m) return m; }
  }
  return appStore.resolvedAccount.children.find((c) => c.id === id) ?? null;
});
const tabName = computed(() => { const t = route.params?.tab; return typeof t === 'string' ? currentModule.value?.tabs?.find((x) => x.slug === t)?.name ?? t : null; });
</script>

<template>
  <header class="app-topbar">
    <div class="brand" :class="{ collapsed: shell.collapsed }"><img :src="logoSrc" alt="" class="logo" /><span class="name">Bridge MES</span></div>
    <nav class="crumbs" aria-label="Breadcrumb">
      <span v-if="categoryLabel" class="c cat">{{ categoryLabel }}</span><span v-if="categoryLabel" class="sep cat">/</span>
      <img v-if="current?.iconUrl" :src="current.iconUrl" alt="" class="crumb-ico" />
      <b v-if="current">{{ current.name }}</b>
      <template v-if="tabName"><span class="sep tab">/</span><span class="c tab">{{ tabName }}</span></template>
    </nav>
    <!-- The one docking point for a module's own controls (see `topbarSlot.ts`): the production scheduler
         teleports its row in here rather than drawing a second bar under this one. Last in the bar since
         round 6 took the attention pill out to the sidebar. -->
    <div id="shell-topbar-slot" class="slot"></div>
  </header>
</template>

<style scoped>
/* z-index 80, not 30: the bar is a stacking context, so it caps every menu a module teleports into the slot.
   The page's own menus sit at 50 (`jr-colmenu`), 60 (`an-rowmenu`/`jr-menu`), 61 (`oc-menu`) and 70
   (`jr-popover`, the jobs configurator, which is anchored in a plain `position: relative` box and so
   competes here) — at 30 a header menu opened *behind* an open jobs-table column menu. 80 clears all of
   them, including the 70 that would otherwise tie and lose on document order. Still below the filters
   drawer (1000), the modals (1000/9999, teleported to body or fixed at the root) and the demo overlay
   (10000); the sidebar (20) and its account menu (40, inside the sidebar's own context) never overlap
   this row. */
.app-topbar { height: var(--shell-topbar-h); display: flex; align-items: center; gap: 14px; padding: 0 14px 0 0; background: var(--panel); border-bottom: 1px solid var(--line); color: var(--ink); position: relative; z-index: 80; }
.brand { width: var(--shell-sidebar-w); flex: 0 0 auto; height: 100%; display: flex; align-items: center; gap: 10px; padding: 0 14px; background: var(--shell); color: var(--shell-ink); font-family: var(--font-display); font-weight: 700; font-size: 19px; transition: width .18s ease; }
.logo { height: 26px; width: auto; }
/* Collapsed, the brand block is the head of the icon column, so it has to be the sidebar's item box and not
   a smaller copy of the expanded block (round 4: "the little bridge logo is bigger than the other logos, and
   therefore it doesn't line up anymore"). The sidebar's geometry, from AppSidebar.vue: a 64px column whose
   `.scroll` pads 8px on both sides and whose collapsed `.item` is `justify-content: center; padding: 9px 0`
   around a 20×20 `.ico`. Mirroring that padding and centring here puts the logo's axis on 32px — the same
   axis as every icon below it — and the 20px square with `object-fit: contain` makes it the same size,
   whatever the artwork's aspect ratio. Expanded is untouched: 26px logo beside the name. */
.brand.collapsed { width: var(--shell-sidebar-w-collapsed); padding: 0 8px; justify-content: center; } .brand.collapsed .name { display: none; }
.brand.collapsed .logo { height: 20px; width: 20px; object-fit: contain; }
/* The breadcrumb gives way by segments, and the module name is not one of them: the owner reads it off this
   row, so `b` neither shrinks nor ellipsises. That makes it the box's floor — no `min-width` is declared,
   because a flex item's automatic minimum is exactly this: the icon plus the whole name (~172px for
   "Production Scheduler"), and whatever separators are still showing. The category and the tab shrink and
   ellipsise ahead of it, and are dropped outright below the widths in the media block. Only under 1100px,
   where the row has nothing left to give, does the name go back to ellipsising. */
.crumbs { display: flex; align-items: center; flex: 0 1 auto; color: var(--muted); font-size: 14px; white-space: nowrap; overflow: hidden; } .crumbs b { flex: 0 0 auto; color: var(--ink); font-weight: 600; overflow: visible; } .crumbs .c { flex: 0 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; } .crumbs .sep { flex: 0 0 auto; margin: 0 6px; }
/* The white bar needs the module icon flattened, not the sidebar's inverted one. */
.crumb-ico { width: 18px; height: 18px; margin-right: 7px; flex: 0 0 auto; filter: brightness(0) opacity(.72); }
/* Module controls dock here, right-aligned. Empty for every module but the scheduler, in which case it is
   simply the flexible gap that ends the bar. */
.slot { flex: 1 1 auto; min-width: 0; display: flex; align-items: center; justify-content: flex-end; gap: 8px; height: 100%; }
/* Segment by segment, in the order the owner would drop them. The widths are set by the widest thing in the
   bar — the scheduler's control row; see 2026-09-07-scheduler-restyle-design.md §3.6 for the arithmetic.
   Round 4 gave the row slack on both sides (the hamburger went, and the scheduler's cluster lost its
   last-update reading to the stats strip) and round 6 gave it the pill's width back, so these steps now fire
   well before they have to. They are left where they were measured: shedding the category at 1600 was never
   about the last 40px. */
@media (max-width: 1600px) { .crumbs .cat { display: none; } }
@media (max-width: 1400px) { .crumbs .tab { display: none; } }
@media (max-width: 1100px) { .crumbs { min-width: 0; } .crumbs b { flex: 0 1 auto; overflow: hidden; text-overflow: ellipsis; } }
@media (max-width: 900px) { .crumbs { display: none; } }
@media (prefers-reduced-motion: reduce) { .brand { transition: none; } }
</style>
